import React from "react";
import { Box, Typography, Divider, TextareaAutosize } from "@mui/material";
import { Close } from "@mui/icons-material";
import PropTypes from "prop-types";

import {
  ButtonV1,
  DropdownV1,
  TextField,
  ModalBox,
  DateTimePicker,
} from "components";

import { editTrackingReasons, OTHER_REASON_VALUE } from "./constants";

import theme from "themeProvider";

const EditTrackingDetailsModal = ({
  open,
  onClose,
  shipmentNumber,
  trackingModalData,
  updateTrackingField,
  isSaveTrackingEnabled,
  trackingUpdateLoading,
  onSave,
}) => {
  const hasDriverNumberError =
    !!trackingModalData.driverNumber &&
    !/^\d{10}$/.test(trackingModalData.driverNumber);

  const handleDriverNumberChange = (e) => {
    const digitsOnly = e.target.value.replace(/\D/g, "").slice(0, 10);
    updateTrackingField("driverNumber", digitsOnly);
  };

  return (
    <ModalBox open={open} onCloseModal={onClose} hideCrossIcon>
      <Box
        width="100%"
        style={{
          borderRadius: 16,
          overflow: "hidden",
        }}
      >
        <Box
          width="100%"
          padding={2}
          style={{
            maxHeight: "80vh",
            overflowY: "auto",
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Box display="flex" flexDirection="column" gap={1}>
              <Typography fontSize={18} fontWeight={600}>
                Update tracking details
              </Typography>
              <Typography fontSize={14} color={theme.palette.text.secondary}>
                Shipment {shipmentNumber}
              </Typography>
            </Box>
            <Close
              onClick={onClose}
              style={{ cursor: "pointer", color: theme.palette.text.secondary }}
            />
          </Box>
          <Divider style={{ marginBottom: 16 }} />

          <Typography
            fontWeight={600}
            color={theme.palette.text.secondary}
            style={{ marginBottom: 8, display: "block", letterSpacing: 0.5 }}
          >
            EDITABLE FIELDS
          </Typography>

          <Box mb={4} width="100%">
            <Typography style={{ marginBottom: 6 }}>Vehicle no</Typography>
            <TextField
              value={trackingModalData.truckNumber}
              onChange={(e) =>
                updateTrackingField("truckNumber", e.target.value)
              }
              customPadding={10}
              sx={{ width: "100%" }}
            />
          </Box>

          <Box mb={4} width="100%">
            <Typography style={{ marginBottom: 6 }}>Driver name</Typography>
            <TextField
              value={trackingModalData.driverName}
              onChange={(e) =>
                updateTrackingField("driverName", e.target.value)
              }
              customPadding={10}
              sx={{ width: "100%" }}
            />
          </Box>

          <Box mb={4} width="100%">
            <Typography style={{ marginBottom: 6 }}>
              Driver contact number
            </Typography>
            <TextField
              value={trackingModalData.driverNumber}
              type="text"
              inputMode="numeric"
              onChange={handleDriverNumberChange}
              customPadding={10}
              sx={{ width: "100%" }}
              highlightError={hasDriverNumberError}
            />
            {hasDriverNumberError && (
              <Typography
                fontSize={12}
                style={{ color: theme.palette.error.dark, marginTop: 4 }}
              >
                Enter exactly 10 digits
              </Typography>
            )}
          </Box>

          <Box mb={4} width="100%">
            <Typography style={{ marginBottom: 8 }}>
              Expected reporting time
            </Typography>
            <DateTimePicker
              value={trackingModalData.expectedReportingTime}
              onChange={(newValue) =>
                updateTrackingField("expectedReportingTime", newValue)
              }
              dateStyle={{ width: "100%" }}
              disablePast={true}
              size="small"
              error={false}
            />
          </Box>

          <Divider style={{ marginBottom: 16 }} />

          <Box mb={4} width="100%">
            <Box display="flex">
              <Typography style={{ marginBottom: 8 }} fontWeight={600}>
                Reason for update
              </Typography>
              <Typography
                style={{ color: theme.palette.error.dark, marginLeft: 4 }}
              >
                *
              </Typography>
            </Box>
            <DropdownV1
              itemList={editTrackingReasons}
              listType="array"
              placeholder="Select a reason..."
              value={trackingModalData.trackingReason}
              onChange={(e) =>
                updateTrackingField("trackingReason", e?.target?.value ?? e)
              }
              sx={{ width: "100%" }}
            />
          </Box>

          {trackingModalData.trackingReason === OTHER_REASON_VALUE && (
            <Box mb={4} width="100%">
              <TextareaAutosize
                placeholder="Describe the reason..."
                style={{
                  width: "100%",
                  borderRadius: 8,
                  border: `1px solid ${theme.palette.grey[400]}`,
                  padding: 8,
                  fontFamily: "inherit",
                  resize: "none",
                  fontSize: 16,
                }}
                value={trackingModalData.trackingOtherReason}
                onChange={(e) =>
                  updateTrackingField("trackingOtherReason", e.target.value)
                }
                minRows={2}
              />
            </Box>
          )}

          <Divider style={{ marginBottom: 16 }} />
          <Box display="flex" justifyContent="flex-end" gap={3}>
            <ButtonV1 variant="outlined" title="Cancel" onClick={onClose} />
            <ButtonV1
              title="Save update"
              disabled={!isSaveTrackingEnabled || hasDriverNumberError}
              loader={trackingUpdateLoading}
              onClick={onSave}
            />
          </Box>
        </Box>
      </Box>
    </ModalBox>
  );
};

EditTrackingDetailsModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  shipmentNumber: PropTypes.string,
  trackingModalData: PropTypes.object,
  updateTrackingField: PropTypes.func,
  isSaveTrackingEnabled: PropTypes.bool,
  trackingUpdateLoading: PropTypes.bool,
  onSave: PropTypes.func,
};

export default React.memo(EditTrackingDetailsModal);
